import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Eye, Camera, Heart, Users, MessageCircle, Star, Award, Crown, CheckCircle, Play, Mic, Globe } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
export function InfluencersPage() {
  return <LanguageProvider data-id="element-4079">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-4080">
        <ScrollProgress data-id="element-4081" />
        <Navbar data-id="element-4082" />
        <main className="w-full" data-id="element-4083">
          <InfluencerHero data-id="element-4084" />
          <WhyHostSection data-id="element-4085" />
          <ContentOpportunitiesSection data-id="element-4086" />
          <CollaborationPackagesSection data-id="element-4087" />
          <ExperienceDetailsSection data-id="element-4088" />
          <SocialProofSection data-id="element-4089" />
          <InfluencerCTA data-id="element-4090" />
        </main>
        <Footer data-id="element-4091" />
      </div>
    </LanguageProvider>;
}
function InfluencerHero() {
  return <section className="relative w-full min-h-screen bg-black flex items-center justify-center overflow-hidden" data-id="element-4092">
      <div className="absolute inset-0" data-id="element-4093">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-30" data-id="element-4094" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" data-id="element-4095"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-4096">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1
      }} data-id="element-4097">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-400/70 hover:text-amber-400 text-sm tracking-wider uppercase font-light mb-12 transition-colors" data-id="element-4098">
            <ArrowLeft className="w-4 h-4" data-id="element-4099" />
            Back to Life in the Sikh Kingdoms
          </Link>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-4100">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6 font-light" data-id="element-4101">
            Presented by Akal Steel
          </p>
          <p className="text-amber-400 tracking-[0.3em] text-xs md:text-sm uppercase mb-8 font-light" data-id="element-4102">
            Host an Exclusive Experience
          </p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-8 tracking-tight leading-none" data-id="element-4103">
            HOST AN
            <br data-id="element-4104" />
            <span className="text-amber-400" data-id="element-4105">EXPERIENCE</span>
          </h1>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-4106"></div>
          <p className="text-xl md:text-2xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-4107">
            Create Unforgettable Content at the Intersection of Culture, Cuisine
            &amp; Immersive Art
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-white/60" data-id="element-4108">
          <div className="text-center" data-id="element-4109">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-4110">
              360°
            </p>
            <p className="font-serif text-xl text-white" data-id="element-4111">Immersive</p>
            <p className="text-xs font-light" data-id="element-4112">Projections</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-4113"></div>
          <div className="text-center" data-id="element-4114">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-4115">
              Royal
            </p>
            <p className="font-serif text-xl text-white" data-id="element-4116">Punjabi</p>
            <p className="text-xs font-light" data-id="element-4117">Cuisine</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-4118"></div>
          <div className="text-center" data-id="element-4119">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-4120">
              200+
            </p>
            <p className="font-serif text-xl text-white" data-id="element-4121">Years of</p>
            <p className="text-xs font-light" data-id="element-4122">Sikh Heritage</p>
          </div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" animate={{
      y: [0, 10, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} data-id="element-4123">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/50 to-transparent" data-id="element-4124"></div>
      </motion.div>
    </section>;
}
function WhyHostSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-4125">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-4126"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-4127">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-4128">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-4129">
            Why This Experience
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-4130">
            WHY <span className="text-amber-400" data-id="element-4131">HOST HERE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-4132"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-4133">
            Life in the Sikh Kingdoms is unlike any venue you've hosted in
            before. It's a living, breathing world — and your audience will feel
            every second of it.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-4134">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-4135">
            <Camera className="w-8 h-8 text-amber-400 mb-4" data-id="element-4136" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-4137">
              Unmatched Visual Content
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-4138">
              360° immersive projections, royal cuisine, and interactive
              technology create content that stops the scroll. Every corner of
              this experience is a frame-worthy moment — from the sensory
              battlefield to the royal court audience.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-4139">
            <Star className="w-8 h-8 text-amber-400 mb-4" data-id="element-4140" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-4141">
              Exclusive Access
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-4142">
              Behind-the-scenes creator previews, private photo moments, and
              early access to the experience before public launch. You'll have
              content no one else has — and the story to match.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-4143">
            <Mic className="w-8 h-8 text-amber-400 mb-4" data-id="element-4144" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-4145">
              Authentic Storytelling
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-4146">
              Connect your audience with 200+ years of Sikh heritage through
              taste, sight, sound, and storytelling. This isn't a sponsored post
              — it's a genuine cultural journey your community will thank you
              for sharing.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-4147">
            <Heart className="w-8 h-8 text-amber-400 mb-4" data-id="element-4148" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-4149">
              Community Engagement
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-4150">
              Align with cultural preservation, youth education, and
              multicultural excellence. A portion of proceeds supports the Sikh
              Art and Culture Gallery at the ROM — giving your audience a reason
              to feel good about attending.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function ContentOpportunitiesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-4151">
      <div className="container mx-auto px-4" data-id="element-4152">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-4153">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-4154">
            What You'll Capture
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-4155">
            CONTENT <span className="text-amber-400" data-id="element-4156">OPPORTUNITIES</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-4157"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light" data-id="element-4158">
            Three distinct content worlds, each more extraordinary than the
            last.
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto space-y-8" data-id="element-4159">
          {/* Immersive Projections */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-amber-400/30 overflow-hidden" data-id="element-4160">
            <div className="aspect-video lg:aspect-auto relative" data-id="element-4161">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" alt="Royal Procession" className="w-full h-full object-cover" data-id="element-4162" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40" data-id="element-4163"></div>
            </div>
            <div className="p-8 md:p-10 bg-black/60 flex flex-col justify-center" data-id="element-4164">
              <div className="flex items-center gap-3 mb-4" data-id="element-4165">
                <Eye className="w-6 h-6 text-amber-400" data-id="element-4166" />
                <p className="text-amber-400 text-xs tracking-[0.2em] uppercase font-light" data-id="element-4167">
                  Visual Content
                </p>
              </div>
              <h3 className="font-serif text-3xl text-white mb-4" data-id="element-4168">
                360° Immersive Projections
              </h3>
              <p className="text-white/70 font-light leading-relaxed mb-4" data-id="element-4169">
                Maharaja Ranjit Singh's royal procession, the Khalsa cavalry,
                twilight at the palace — all rendered in breathtaking 360°
                projection across walls, floors, and tables. Every angle is a
                hero shot.
              </p>
              <div className="space-y-2" data-id="element-4170">
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-4171">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-4172"></span>
                  Royal procession visuals
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-4173">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-4174"></span>
                  Khalsa army panoramas
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-4175">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-4176"></span>
                  Palace architecture &amp; twilight scenes
                </div>
              </div>
            </div>
          </motion.div>

          {/* Culinary Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-amber-400/30 overflow-hidden" data-id="element-4177">
            <div className="p-8 md:p-10 bg-black/60 flex flex-col justify-center order-2 lg:order-1" data-id="element-4178">
              <div className="flex items-center gap-3 mb-4" data-id="element-4179">
                <Crown className="w-6 h-6 text-amber-400" data-id="element-4180" />
                <p className="text-amber-400 text-xs tracking-[0.2em] uppercase font-light" data-id="element-4181">
                  Food Content
                </p>
              </div>
              <h3 className="font-serif text-3xl text-white mb-4" data-id="element-4182">
                Royal Punjabi Cuisine
              </h3>
              <p className="text-white/70 font-light leading-relaxed mb-4" data-id="element-4183">
                Historically researched dishes from Maharaja Ranjit Singh's
                court — styled, plated, and served in an immersive setting. Chef
                interactions, dish reveals, and the story behind every course.
              </p>
              <div className="space-y-2" data-id="element-4184">
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-4185">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-4186"></span>
                  Styled royal dish reveals
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-4187">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-4188"></span>
                  Chef interactions &amp; behind-the-scenes
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-4189">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-4190"></span>
                  5–7 course immersive dining journey
                </div>
              </div>
            </div>
            <div className="aspect-video lg:aspect-auto relative order-1 lg:order-2" data-id="element-4191">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2zSbggEuZfU5JNU7W478R9/Gemini_Generated_Image_36dj2y36dj2y36dj.png" alt="Royal Cuisine" className="w-full h-full object-cover" data-id="element-4192" />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/40" data-id="element-4193"></div>
            </div>
          </motion.div>

          {/* Interactive Moments */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-amber-400/30 overflow-hidden" data-id="element-4194">
            <div className="aspect-video lg:aspect-auto relative" data-id="element-4195">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/7baikykwbzRgddsVg9e56P/Gemini_Generated_Image_k59hd9k59hd9k59h.png" alt="Interactive Experience" className="w-full h-full object-cover" data-id="element-4196" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40" data-id="element-4197"></div>
            </div>
            <div className="p-8 md:p-10 bg-black/60 flex flex-col justify-center" data-id="element-4198">
              <div className="flex items-center gap-3 mb-4" data-id="element-4199">
                <Play className="w-6 h-6 text-amber-400" data-id="element-4200" />
                <p className="text-amber-400 text-xs tracking-[0.2em] uppercase font-light" data-id="element-4201">
                  Interactive Moments
                </p>
              </div>
              <h3 className="font-serif text-3xl text-white mb-4" data-id="element-4202">
                Sensory &amp; Interactive Technology
              </h3>
              <p className="text-white/70 font-light leading-relaxed mb-4" data-id="element-4203">
                Voice-activated royal court audiences, sensory battlefields with
                scent triggers and spatial audio, soaring eagle perspectives —
                moments your audience will watch on repeat.
              </p>
              <div className="space-y-2" data-id="element-4204">
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-4205">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-4206"></span>
                  Sensory battlefield experience
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-4207">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-4208"></span>
                  Voice-activated royal court audience
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-4209">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-4210"></span>
                  Soaring eagle perspectives
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function CollaborationPackagesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-4211">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-4212"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-4213">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-4214">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-4215">
            How We Work Together
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-4216">
            COLLABORATION <span className="text-amber-400" data-id="element-4217">PACKAGES</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-4218"></div>
          <p className="text-white/70 font-light max-w-2xl mx-auto" data-id="element-4219">
            From intimate creator previews to full cultural ambassador
            partnerships, we have a collaboration that fits your vision.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-4220">
          {/* Creator Preview */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0
        }} data-id="element-4221">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-4222">
              <div className="text-center mb-6" data-id="element-4223">
                <Camera className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-4224" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-4225">
                  Creator Preview
                </h3>
                <p className="text-amber-400/60 text-xs tracking-wider uppercase font-light" data-id="element-4226">
                  Early Access
                </p>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-4227"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-4228">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4229">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4230"></span>
                  Early access preview night before public launch
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4231">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4232"></span>
                  Behind-the-scenes content creation time
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4233">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4234"></span>
                  2 complimentary tickets
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4235">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4236"></span>
                  Social media cross-promotion
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4237">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4238"></span>
                  Digital press kit &amp; assets
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20a%20Creator%20Preview%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-4239">
                Apply Now
              </motion.a>
            </div>
          </motion.div>

          {/* Experience Host */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="relative" data-id="element-4240">
            <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-4241">
              <div className="bg-amber-400 text-black font-bold py-2 px-6 flex items-center gap-2" data-id="element-4242">
                <Star className="w-4 h-4" fill="currentColor" data-id="element-4243" />
                <span className="text-xs uppercase tracking-wider" data-id="element-4244">
                  Most Popular
                </span>
              </div>
            </div>
            <div className="border-2 border-amber-400 bg-black/80 p-8 pt-12 h-full flex flex-col" data-id="element-4245">
              <div className="text-center mb-6" data-id="element-4246">
                <Users className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-4247" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-4248">
                  Experience Host
                </h3>
                <p className="text-amber-400/60 text-xs tracking-wider uppercase font-light" data-id="element-4249">
                  Private Table
                </p>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-4250"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-4251">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4252">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4253"></span>
                  Private hosted table for your community (10 guests)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4254">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4255"></span>
                  Dedicated content creation time &amp; access
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4256">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4257"></span>
                  Professional photography of your experience
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4258">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4259"></span>
                  Featured in event social media channels
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4260">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4261"></span>
                  Custom branded moment at the event
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20hosting%20an%20influencer%20event%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full bg-amber-400 hover:bg-amber-500 text-black py-3 text-center text-sm tracking-wider uppercase font-medium transition-colors duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-4262">
                Apply Now
              </motion.a>
            </div>
          </motion.div>

          {/* Cultural Ambassador */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-4263">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-4264">
              <div className="text-center mb-6" data-id="element-4265">
                <Globe className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-4266" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-4267">
                  Cultural Ambassador
                </h3>
                <p className="text-amber-400/60 text-xs tracking-wider uppercase font-light" data-id="element-4268">
                  Full Partnership
                </p>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-4269"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-4270">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4271">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4272"></span>
                  All Experience Host benefits included
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4273">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4274"></span>
                  Multi-event access (May–Dec 2026)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4275">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4276"></span>
                  Co-branded content series opportunity
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4277">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4278"></span>
                  Input into future cultural experiences
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-4279">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-4280"></span>
                  Recognition as Cultural Ambassador
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20becoming%20a%20Cultural%20Ambassador%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-4281">
                Apply Now
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function ExperienceDetailsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-4282">
      <div className="container mx-auto px-4" data-id="element-4283">
        <div className="max-w-5xl mx-auto" data-id="element-4284">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="border-2 border-amber-400/40 p-8 md:p-12 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-4285">
            <div className="text-center mb-10" data-id="element-4286">
              <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-4" data-id="element-4287">
                Event Details
              </p>
              <h2 className="font-serif text-4xl text-white mb-4" data-id="element-4288">
                The <span className="text-amber-400" data-id="element-4289">Experience</span>
              </h2>
              <div className="w-16 h-px bg-amber-400/30 mx-auto" data-id="element-4290"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10" data-id="element-4291">
              <div className="text-center border-l-2 border-amber-400/30 pl-6" data-id="element-4292">
                <p className="text-amber-400 text-xs tracking-wider uppercase mb-2" data-id="element-4293">
                  Venue
                </p>
                <p className="font-serif text-xl text-white mb-1" data-id="element-4294">
                  Illuminarium Toronto
                </p>
                <p className="text-white/50 text-sm font-light" data-id="element-4295">Toronto, ON</p>
              </div>
              <div className="text-center border-l-2 border-amber-400/30 pl-6" data-id="element-4296">
                <p className="text-amber-400 text-xs tracking-wider uppercase mb-2" data-id="element-4297">
                  When
                </p>
                <p className="font-serif text-xl text-white mb-1" data-id="element-4298">
                  Summer 2026
                </p>
                <p className="text-white/50 text-sm font-light" data-id="element-4299">
                  Monthly May–Dec 2026
                </p>
              </div>
              <div className="text-center border-l-2 border-amber-400/30 pl-6" data-id="element-4300">
                <p className="text-amber-400 text-xs tracking-wider uppercase mb-2" data-id="element-4301">
                  Format
                </p>
                <p className="font-serif text-xl text-white mb-1" data-id="element-4302">90 Minutes</p>
                <p className="text-white/50 text-sm font-light" data-id="element-4303">
                  Immersive Dining
                </p>
              </div>
            </div>
            <div className="text-center pt-8 border-t border-white/10" data-id="element-4304">
              <p className="text-xl text-amber-400 italic font-light" data-id="element-4305">
                "Step inside a living, breathing version of the Sikh Kingdoms —
                where you don't just see history, you experience it."
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function SocialProofSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-4306">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-4307"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-4308">
        <div className="max-w-4xl mx-auto" data-id="element-4309">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-16" data-id="element-4310">
            <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-4311">
              The Legacy Behind the Experience
            </p>
            <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-4312">
              BUILT ON <span className="text-amber-400" data-id="element-4313">EXCELLENCE</span>
            </h2>
            <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-4314"></div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="border-2 border-amber-400/40 p-8 md:p-12 bg-gradient-to-br from-amber-950/20 to-black/60 mb-8" data-id="element-4315">
            <p className="text-2xl text-amber-400 font-light italic text-center leading-relaxed" data-id="element-4316">
              "This immersive experience honors the groundbreaking Art of the
              Sikh Kingdoms exhibition at the Royal Ontario Museum — the second
              most popular exhibition in ROM history."
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4317">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-4318">
              <Award className="w-8 h-8 text-amber-400 mx-auto mb-3" data-id="element-4319" />
              <h4 className="font-serif text-lg text-white mb-2" data-id="element-4320">
                Oscar-Winning Animators
              </h4>
              <p className="text-white/60 text-sm font-light" data-id="element-4321">
                The team behind Lord of the Rings
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-4322">
              <Star className="w-8 h-8 text-amber-400 mx-auto mb-3" data-id="element-4323" />
              <h4 className="font-serif text-lg text-white mb-2" data-id="element-4324">
                Emmy-Winning Artists
              </h4>
              <p className="text-white/60 text-sm font-light" data-id="element-4325">
                Creative minds behind Hasbro's global installations
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-4326">
              <Crown className="w-8 h-8 text-amber-400 mx-auto mb-3" data-id="element-4327" />
              <h4 className="font-serif text-lg text-white mb-2" data-id="element-4328">
                HBO Producer
              </h4>
              <p className="text-white/60 text-sm font-light" data-id="element-4329">
                Game of Thrones production pedigree
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function InfluencerCTA() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-4330">
      <div className="container mx-auto px-4" data-id="element-4331">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto text-center" data-id="element-4332">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-6" data-id="element-4333" />
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6" data-id="element-4334">
            Ready to Create Something{' '}
            <span className="text-amber-400" data-id="element-4335">Extraordinary?</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-4336"></div>
          <p className="text-xl text-white/70 font-light mb-10 leading-relaxed" data-id="element-4337">
            We're looking for creators who are passionate about culture,
            heritage, and authentic storytelling. Let's build something your
            audience will remember.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-4338">
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20hosting%20an%20influencer%20event%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-4339">
              <MessageCircle className="w-5 h-5" data-id="element-4340" />
              Apply via WhatsApp
            </motion.a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border-2 border-amber-400/50 hover:border-amber-400 text-white hover:text-amber-400 px-8 py-4 font-light tracking-wider uppercase text-sm transition-all duration-300" data-id="element-4341">
              <ArrowLeft className="w-4 h-4" data-id="element-4342" />
              Back to Main Site
            </Link>
          </div>
          <p className="text-white/40 text-sm font-light mt-6" data-id="element-4343">
            Our team will respond within 24 hours to discuss collaboration
            opportunities.
          </p>
        </motion.div>
      </div>
    </motion.section>;
}